import type { FC } from 'react';
import { lazy } from 'react';
import { Navigate, Route, useLocation, useParams } from 'react-router';
import type { TransactionsParams } from 'src/apis/capacity-management/types';
import { useSurrenderRoot } from 'src/apis/surrender-service/use-surrender-root';
import { NetworkPointRedirect } from 'src/components/domain-specifics/network-points/network-point-redirect';
import { ThrowNotFound } from 'src/components/feedback/not-found';
import { AutoFullwidth } from 'src/components/layout/auto-fullwidth';
import { RootRoutes } from 'src/components/root-routes';
import { PageSpinner } from 'src/components/spinner-container';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';
import { stringifySearchParams } from 'src/hooks/use-page-params-from-schema';
import { useStorybook } from 'src/hooks/use-storybook';
import { CompanyTransactions } from 'src/pages/company-transactions';
import { Dashboard } from 'src/pages/dashboard';
import { Debug } from 'src/pages/debug';
import { DeveloperTools } from 'src/pages/developer-tools';
import { useDeveloperTools } from 'src/pages/developer-tools/use-developer-tools';
import { ManageDirectFcfs } from 'src/pages/direct-fcfs';
import { Gdm } from 'src/pages/gdm';
import { H2 } from 'src/pages/h2';
import { Lng } from 'src/pages/lng';
import { Login } from 'src/pages/login';
import { Logout } from 'src/pages/logout';
import { MarketMessages } from 'src/pages/market-messages';
import { ModulationServiceAuctions } from 'src/pages/modulation-service-auctions';
import { Operators } from 'src/pages/operators';
import { FinancialSecurityAmend } from 'src/pages/operators/assignments/details/financial-securities/amend';
import { FinancialSecurityRelease } from 'src/pages/operators/assignments/details/financial-securities/release';
import { UserInvitation } from 'src/pages/organisation/users/invitation';
import { Platform } from 'src/pages/platform';
import { Registration } from 'src/pages/registration';
import { ReportingAuctions } from 'src/pages/reporting/auctions';
import { ReportingAuction } from 'src/pages/reporting/auctions/details';
import { ReportingBalancingGroupAllocations } from 'src/pages/reporting/balancing-group-allocations';
import { BgaRedirect } from 'src/pages/reporting/balancing-group-allocations/balancing-group-route-redirect';
import { ReportingBooking } from 'src/pages/reporting/direct-fcfs/bookings';
import { ReportingLngRoutes } from 'src/pages/reporting/lng';
import { MonthlyStatements } from 'src/pages/reporting/monthly-statements';
import { MonthlyStatementRedirect } from 'src/pages/reporting/monthly-statements/monthly-statement-redirect';
import { DealAllocationToBalancingGroups } from 'src/pages/reporting/my-transactions/deals/allocation-to-balancing-groups';
import { CreateConversion } from 'src/pages/reporting/my-transactions/deals/conversions/create';
import { ConversionDetail } from 'src/pages/reporting/my-transactions/deals/conversions/details';
import { MultiYearTransactionDetail } from 'src/pages/reporting/my-transactions/multi-year';
import { MultiYearAllocationToBalancingGroups } from 'src/pages/reporting/my-transactions/multi-year/allocation-to-balancing-groups';
import { MyTransactionsRedirect } from 'src/pages/reporting/my-transactions/my-transactions-redirect';
import { TradeAllocationToBalancingGroups } from 'src/pages/reporting/my-transactions/trades/allocation-to-balancing-groups';
import { FcfsBookingDetails } from 'src/pages/reporting/regular-fcfs/details';
import { FcfsBookingDetailsByBookingId } from 'src/pages/reporting/regular-fcfs/details/by-booking-id';
import { ReportingPrimaryRemitReports } from 'src/pages/reporting/remit-reports';
import { CreateReshuffleRequest } from 'src/pages/reporting/reshuffles/create';
import { ReshuffleRequestDetails } from 'src/pages/reporting/reshuffles/details';
import { ReportingSecondaryTradings } from 'src/pages/reporting/secondary-trading/overview';
import { ReportingSecondaryTradeProposal } from 'src/pages/reporting/secondary-trading/trade-proposal';
import { ShipperTransactions } from 'src/pages/reporting/shipper-transactions';
import { RedirectAuctionByShipper } from 'src/pages/reporting/shipper-transactions/redirect-auction-by-shipper';
import { ReportingStandard } from 'src/pages/reporting/standard';
import { CreateSurrenderRequest as CreateLegacySurrenderRequest } from 'src/pages/reporting/surrender-legacy/create';
import { SurrenderRequestDetail as LegacySurrenderRequestDetail } from 'src/pages/reporting/surrender-legacy/detail';
import { SurrenderOverview as LegacySurrenderOverview } from 'src/pages/reporting/surrender-legacy/overview';
import {
  CreateBundleSurrenderRequest,
  CreateCamaSurrenderRequest,
  CreateSurrenderRequest,
} from 'src/pages/reporting/surrender/create';
import { SurrenderDetails } from 'src/pages/reporting/surrender/details';
import { TransparencyReporting } from 'src/pages/reporting/transparency-reporting';
import { Settings } from 'src/pages/settings';
import { ShippersView } from 'src/pages/shippers';
import { ShipperDetails } from 'src/pages/shippers/details';
import { EditShipperDocument } from 'src/pages/shippers/documents/edit-document';
import { UploadDocumentsToShippers } from 'src/pages/shippers/documents/upload-documents';
import { TsoInvoiceDetail } from 'src/pages/shippers/invoices/detail';
import { SnamBidCollectorBiddingPage } from 'src/pages/snam-bid-collector/bidding';
import { SnamBiddingSuccessPage } from 'src/pages/snam-bid-collector/bidding/success';
import { Start } from 'src/pages/start';
import { StorageRoot } from 'src/pages/storage';
import { AuctionsStart } from 'src/pages/transport/auctions';
import { CreateComfortBids } from 'src/pages/transport/auctions/comfort-bids/create';
import { EditComfortBids } from 'src/pages/transport/auctions/comfort-bids/edit';
import { AuctionDetailPage } from 'src/pages/transport/auctions/details';
import { RunningAuctionsOverview } from 'src/pages/transport/auctions/overview';
import { ManagePriceStepsChange } from 'src/pages/transport/auctions/price-steps-change-request';
import { RunningAuctionsOverviewTAS } from 'src/pages/transport/auctions/tiles';
import { AuctionTAS } from 'src/pages/transport/auctions/tiles/details';
import { BiddingActivity } from 'src/pages/transport/auctions/tiles/details/bidding-activities';
import { SnamBidCollector } from 'src/pages/transport/bid-collector';
import { BundleRequestsOverview } from 'src/pages/transport/bundle-requests/overview';
import { FcfsBooking } from 'src/pages/transport/fcfs-booking';
import { NetworkPoint } from 'src/pages/transport/network-points/details';
import { NetworkPointsOverview } from 'src/pages/transport/network-points/overview';
import { NetworkPointsRedirect } from 'src/pages/transport/network-points/redirect';
import { TransportProducts } from 'src/pages/transport/products';
import { SecondaryTradingOverview } from 'src/pages/transport/secondary-trading/overview';
import { TransportSecondaryTradingsTrade } from 'src/pages/transport/secondary-trading/trade';
import { NotificationsRoot } from 'src/pages/your-timeline-notifications';
import { ParamsGuard } from 'src/utils/params-guard';

const UnknownError = lazy(() => import('src/pages/errors/unknown'));

const MarketInformation = lazy(() => import('src/pages/market-information'));

const ReportingRedirect: FC = () => {
  /* ISR-1933 TEMP FIX: Redirect to /reporting/auctions due to long loading times (>10s) of the standard report on PROD/TEST */
  return <Navigate replace to="/reporting/auctions" />;
  /*
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  if (monolithUser?.isTso) {
    return <Navigate replace to="/reporting/auctions" />;
  } else {
    return <Navigate replace to="/reporting/standard" />;
  }
  */
};

export const OurRoutes: FC = () => {
  const developerTools = useDeveloperTools();
  const { isStorybook } = useStorybook();
  return (
    <RootRoutes>
      <Route
        path="/platform"
        element={
          isStorybook ? (
            <p>
              This is an Angular page. This page is <strong>not</strong>{' '}
              documentend in Storybook.
            </p>
          ) : (
            <Platform />
          )
        }
      />

      <Route path="/login/*" element={<Login />} />

      <Route path="/logout" element={<Logout />} />

      <Route path="/registration/*" element={<Registration />} />

      <Route
        path="/organisation/users/invitation/:invitationId/*"
        element={<UserInvitation />}
      />

      <Route
        path="/network-points/:oldNetworkPointId/*"
        element={<RedirectOldNetworkPointPages />}
      />

      <Route
        path="/mail-redirect/surrender-service/surrender-request/:surrenderRequestId"
        element={<RedirectMailToSurrenderDetails />}
      />

      <Route
        path="/mail-redirect/capacity-management/shipper-missing-bga-for-deal/:dealId/:direction"
        element={<RedirectDealAllocationToBGA />}
      />

      <Route
        path="/mail-redirect/capacity-management/shipper-missing-bga-for-trade/:tradeId/:direction"
        element={<RedirectTradeAllocationToBGA />}
      />

      <Route
        path="/mail-redirect/capacity-management/bga-overview/:nwpUuid/:balancingGroupId/:balancingGroupName/:allocationIntervalIntersectionStart/:allocationIntervalIntersectionEnd"
        element={<BgaRedirect />}
      />

      <Route
        path="/mail-redirect/capacity-management/conversion/:conversionId"
        element={<RedirectConversion />}
      />

      <Route
        path="/mail-redirect/auction-by-shipper/:shipperId/:assignedAuctionId"
        element={<RedirectAuctionByShipper />}
      />

      <Route
        path="/mail-redirect/ttpc-modulation-service/auction/:auctionId"
        element={<RedirectTtpcModulationServiceAuction />}
      />

      <Route
        path="/mail-redirect/contract-management/shippers/:shipperId/:contractType/:contractId"
        element={
          <ParamsGuard name="contractType" regExp={/^(lect|dect)$/}>
            <RedirectToContractDetailsForTso />
          </ParamsGuard>
        }
      />

      <Route
        path="/mail-redirect/contract-management/operators/:operatorUuid/:contractType/:contractId"
        element={
          <ParamsGuard name="contractType" regExp={/^(lect|dect)$/}>
            <RedirectToContractDetailsForShipper />
          </ParamsGuard>
        }
      />

      <Route path="/umm/*" element={<MarketMessages />} />

      <Route path="/market-messages/*" element={<MarketMessages />} />

      <Route
        path="/reporting/*"
        element={
          <>
            <AutoFullwidth />
            <RootRoutes>
              <Route
                path="/reporting/tr/*"
                element={<TransparencyReporting />}
              />
              <Route
                path="/reporting/monthly-statements/*"
                element={<MonthlyStatements />}
              />
              <Route
                path="/reporting/balancing-group-allocations"
                element={<ReportingBalancingGroupAllocations />}
              />
              <Route
                path="/reporting/shipper-transactions/conversions/:conversionRequestId"
                element={<ConversionDetail />}
              />
              <Route
                path="/reporting/shipper-transactions/multi-year/details/:multiYearAllocationId"
                element={<MultiYearTransactionDetail />}
              />
              <Route
                path="/reporting/shipper-transactions/*"
                element={<ShipperTransactions />}
              />
              <Route
                path="/reporting/standard/*"
                element={<ReportingStandard />}
              />
              <Route
                path="/reporting/surrenders"
                element={<LegacySurrenderOverview />}
              />
              <Route
                path="/reporting/auctions/details/:auctionId"
                element={<ReportingAuction />}
              />
              {[
                '/reporting/auctions',
                '/reporting/auctions/short-and-long-term-auctions',
                '/reporting/auctions/reverse-auctions',
              ].map((path) => (
                <Route key={path} path={path} element={<ReportingAuctions />} />
              ))}
              <Route
                path="/reporting/secondary/proposals/:proposalId"
                element={<ReportingSecondaryTradeProposal />}
              />
              <Route
                path="/reporting/secondary/trades/:tradeId"
                element={<TransportSecondaryTradingsTrade />}
              />
              <Route
                path="/reporting/secondary/*"
                element={<ReportingSecondaryTradings />}
              />
              {[
                '/reporting/remit-reports',
                '/reporting/remit-reports/reports',
                '/reporting/remit-reports/tso-reports',
              ].map((path) => (
                <Route
                  key={path}
                  path={path}
                  element={<ReportingPrimaryRemitReports />}
                />
              ))}
              <Route
                path="/reporting/my-transactions/multi-year/details/:multiYearAllocationId"
                element={<MultiYearTransactionDetail />}
              />
              <Route
                path="/reporting/my-transactions/deals/:dealId/conversions/create/*"
                element={<CreateConversion />}
              />
              <Route
                path="/reporting/my-transactions/deals/:dealId/allocation-to-balancing-groups/*"
                element={<DealAllocationToBalancingGroups />}
              />
              <Route
                path="/reporting/my-transactions/trades/:id/allocation-to-balancing-groups/*"
                element={<TradeAllocationToBalancingGroups />}
              />
              <Route
                path="/reporting/my-transactions/multi-year/:multiYearAllocationId/allocation-to-balancing-groups/*"
                element={<MultiYearAllocationToBalancingGroups />}
              />
              <Route
                path="/reporting/my-transactions/conversions/:conversionRequestId"
                element={<ConversionDetail />}
              />
              <Route
                path="/reporting/my-transactions/surrender/create/cama/:transactionId/*"
                element={<CreateCamaSurrenderRequest />}
              />
              <Route
                path="/reporting/my-transactions/surrender/create/bundle/:bundleId/*"
                element={<CreateBundleSurrenderRequest />}
              />
              <Route
                path="/reporting/my-transactions/surrender/create/:networkPointId/*"
                element={<CreateSurrenderRequest />}
              />
              <Route
                path="/reporting/my-transactions/surrender/details/:surrenderRequestId"
                element={<SurrenderDetails />}
              />
              <Route
                path="/reporting/shipper-transactions/surrender/details/:surrenderRequestId"
                element={<SurrenderDetails />}
              />
              <Route
                path="/reporting/my-transactions/*"
                element={<MyTransactionsRedirect />}
              />
              <Route
                path="/reporting/reshuffles/create/:allocationId/*"
                element={<CreateReshuffleRequest />}
              />
              <Route
                path="/reporting/reshuffles/:reshuffleRequestId"
                element={<ReshuffleRequestDetails />}
              />
              <Route
                path="/reporting/surrenders/create/:transactionId"
                element={<RedirectLegacyToRefactoredSurrenderCamaCreate />}
              />
              <Route
                path="/reporting/surrenders/:surrenderRequestId"
                element={<RedirectLegacyToRefactoredSurrenderDetails />}
              />
              <Route
                path="/reporting/fcfs"
                element={
                  <Navigate
                    replace
                    to={{
                      pathname: '/reporting/my-transactions',
                      search: stringifySearchParams({
                        transactionType: ['FCFS'],
                      } satisfies TransactionsParams),
                    }}
                  />
                }
              />
              <Route
                path="/reporting/my-transactions/direct-fcfs/bookings/:bookingId"
                element={<ReportingBooking />}
              />
              <Route
                path="/reporting/my-transactions/regular-fcfs/details/:bookingProcessId"
                element={<FcfsBookingDetails />}
              />
              <Route
                path="/reporting/my-transactions/regular-fcfs/details-by-booking-id/:bookingId"
                element={<FcfsBookingDetailsByBookingId />}
              />
              <Route
                path="/reporting/shipper-transactions/regular-fcfs/details/:bookingProcessId"
                element={<FcfsBookingDetails />}
              />
              <Route
                path="/reporting/shipper-transactions/regular-fcfs/details-by-booking-id/:bookingId"
                element={<FcfsBookingDetailsByBookingId />}
              />
              <Route path="/reporting/lng/*" element={<ReportingLngRoutes />} />
              <Route path="/reporting" element={<ReportingRedirect />} />
              <Route path="*" element={<ThrowNotFound />} />
            </RootRoutes>
          </>
        }
      />

      <Route path="/company-transactions/*" element={<CompanyTransactions />} />

      <Route
        path="/transport/snam-bid-collector/*"
        element={<SnamBidCollector />}
      />
      <Route
        path="/snam-bid-collector/*"
        element={
          <RedirectPrefix
            from="/snam-bid-collector"
            to="/transport/snam-bid-collector"
          />
        }
      />

      <Route path="/snam-bidding" element={<SnamBidCollectorBiddingPage />} />
      <Route
        path="/snam-bidding/success"
        element={<SnamBiddingSuccessPage />}
      />

      <Route path="/settings/*" element={<Settings />} />

      <Route
        path="/profile"
        element={<Navigate replace to="/settings/profile" />}
      />

      <Route path="/dashboard" element={<Dashboard />} />

      <Route
        path="/mail-redirect/capacity-management/tso-new-invoice-created-for-monthly-statement/:monthlyStatementId"
        element={<MonthlyStatementRedirect />}
      />

      <Route
        path="/mail-redirect/capacity-management/tso-new-invoice-created/:invoiceId"
        element={<RedirectShippersInvoice />}
      />

      <Route
        path="/shippers/invoices/:invoiceId"
        element={<TsoInvoiceDetail />}
      />

      <Route
        path="/shippers/details/:shipperRefId/admins"
        element={<RedirectShipperAdmins />}
      />

      <Route
        path="/shippers/details/:shipperRefId/*"
        element={<RedirectShipper />}
      />

      <Route
        path="/shippers/documents/:operatorDocumentId"
        element={<EditShipperDocument />}
      />

      {[
        '/shippers/list/*',
        '/shippers/documents/*',
        '/shippers/invoices/*',
        '/shippers/credit-limit/*',
        '/shippers/organisations-and-users/*',
      ].map((path) => (
        <Route key={path} path={path} element={<ShippersView />} />
      ))}

      <Route
        path="/shippers"
        element={<Navigate replace to="/shippers/organisations-and-users" />}
      />

      <Route
        path="/shippers/organisations"
        element={<Navigate replace to="/shippers/organisations-and-users" />}
      />

      {/* Redirects users from the old /shippers/users page to the new one (in case they have it bookmarked) */}
      <Route
        path="/shippers/users"
        element={<Navigate replace to="/shippers/organisations-and-users" />}
      />

      <Route
        path="/shippers/upload-documents"
        element={<UploadDocumentsToShippers />}
      />

      {[
        '/shippers/:shipperRefId/*',
        '/mail/shippers/:shipperRefId/*',
        '/dashboard/shippers/:shipperUuid/*',
      ].map((path) => (
        <Route key={path} path={path} element={<ShipperDetails />} />
      ))}

      <Route path="/lng/*" element={<Lng />} />

      <Route path="/h2/*" element={<H2 />} />

      <Route path="/storage/*" element={<StorageRoot />} />

      <Route
        path="/dashboard/operators/assignments/:operatorUuid/financial-securities/cash-deposit/:financialSecurityId/release"
        element={<FinancialSecurityRelease type="CASH_DEPOSIT" />}
      />
      <Route
        path="/dashboard/operators/assignments/:operatorUuid/financial-securities/cash-deposit/:financialSecurityId/amend"
        element={<FinancialSecurityAmend type="CASH_DEPOSIT" />}
      />
      <Route
        path="/dashboard/operators/assignments/:operatorUuid/financial-securities/rating/:financialSecurityId/amend"
        element={<FinancialSecurityAmend type="RATING" />}
      />
      <Route
        path="/dashboard/operators/assignments/:operatorUuid/financial-securities/bank-guarantee/:financialSecurityId/amend"
        element={<FinancialSecurityAmend type="BANK_GUARANTEE" />}
      />
      <Route
        path="/dashboard/operators/assignments/:operatorUuid/financial-securities/parent-company-guarantee/:financialSecurityId/amend"
        element={<FinancialSecurityAmend type="PARENT_COMPANY_GUARANTEE" />}
      />

      {['/operators/*', '/dashboard/operators/*'].map((path) => (
        <Route key={path} path={path} element={<Operators />} />
      ))}

      <Route
        path="/mail-redirect/capacity-management/shipper-new-invoice-document-available/:invoiceId"
        element={<RedirectOperatorsInvoice />}
      />

      <Route
        path="/mail-redirect/capacity-management/shipper-invoice-cancelled/:invoiceId"
        element={<RedirectOperatorsInvoice />}
      />

      <Route
        path="/mail-redirect/capacity-management/shipper-new-invoice-available/:invoiceId"
        element={<RedirectOperatorsInvoice />}
      />

      <Route
        path="/assignments/*"
        element={<Navigate replace to="/operators/assignments" />}
      />

      <Route
        path="/operator-documents"
        element={<Navigate replace to="/operators/operator-documents" />}
      />

      <Route path="/transport/products/*" element={<TransportProducts />} />

      <Route
        path="/reporting/products/*"
        element={
          <RedirectPrefix from="/reporting/products" to="/transport/products" />
        }
      />

      <Route
        path="/transport/secondary/*"
        element={<SecondaryTradingOverview />}
      />

      <Route
        path="/transport/secondary/trades/:tradeId"
        element={<TransportSecondaryTradingsTrade />}
      />

      <Route
        path="/transport/secondary/proposals/:proposalId"
        element={<ReportingSecondaryTradeProposal />}
      />

      <Route
        path="/mail-redirect/secondary-market-service/bulletin-board/:bulletinBoardNoticeId"
        element={<RedirectSecondaryBulletinBoard />}
      />

      <Route
        path="/mail-redirect/secondary-market-service/bulletin-board/:bulletinBoardNoticeId/:conversationId"
        element={<RedirectSecondaryBulletinBoardConversation />}
      />

      <Route
        path="/transport/network-points"
        element={<NetworkPointsOverview />}
      />

      <Route
        path="/transport/network-points/redirect/:networkPointId"
        element={<NetworkPointsRedirect />}
      />

      <Route
        path="/transport/network-points/:oldNetworkPointId/*"
        element={<NetworkPoint />}
      />

      <Route
        path="/network-points-redirect/:networkPointUuid"
        element={<NetworkPointRedirect />}
      />

      <Route path="/transport/auctions" element={<AuctionsStart />} />

      <Route
        path="/transport/auctions/comfort-bids/create/*"
        element={<CreateComfortBids />}
      />

      <Route
        path="/transport/auctions/comfort-bids/edit/:bidId"
        element={<EditComfortBids />}
      />

      <Route
        path="/transport/auctions/list"
        element={<RunningAuctionsOverview />}
      />

      <Route
        path="/transport/auctions/tiles/:tileId"
        element={<RunningAuctionsOverviewTAS />}
      />

      <Route
        path="/transport/auctions/:auctionId/price-steps-change-request"
        element={<ManagePriceStepsChange />}
      />

      <Route
        path="/transport/auctions/tiles/:tileId/:auctionId/bidding-activities/:biddingActivityId"
        element={<BiddingActivity />}
      />

      <Route
        path="/transport/auctions/tiles/:tileId/:auctionId"
        element={<AuctionTAS />}
      />

      {[
        '/transport/auctions/:auctionId',
        '/transport/auctions/:auctionId/:orderId',
      ].map((path) => (
        <Route key={path} path={path} element={<AuctionDetailPage />} />
      ))}

      <Route path="/transport/fcfs-booking/*" element={<FcfsBooking />} />

      <Route
        path="/transport/bundle-requests/*"
        element={<BundleRequestsOverview />}
      />

      <Route path="/direct-fcfs/*" element={<ManageDirectFcfs />} />
      <Route
        path="/transport/manage-direct-fcfs/*"
        element={<ManageDirectFcfs />}
      />

      <Route path="/market-information/*" element={<MarketInformation />} />

      <Route path="/gdm/*" element={<Gdm />} />

      <Route
        path="/modulation-service-auctions/*"
        element={<ModulationServiceAuctions />}
      />

      <Route path="/notifications/*" element={<NotificationsRoot />} />

      <Route path="/error" element={<UnknownError />} />

      {developerTools && (
        <Route path="/developer-tools" element={<DeveloperTools />} />
      )}

      {PRISMA_CONFIG.stage === 'storybook' && (
        <Route path="/debug/*" element={<Debug />} />
      )}

      <Route
        path="/"
        element={
          PRISMA_CONFIG.stage === 'local' &&
          !PRISMA_CONFIG.angularUrl.includes('localhost:4200') ? (
            <div className="grid items-center gap-md">
              <p>Hello Developer!</p>

              <p>
                This is the root page of the React frontend. Normally we
                redirect the user to the Angular start page at this point, but
                it looks like you're running only the React frontend locally, so
                we keep you on this page.
              </p>
              <p>
                If you need the Angular frontend as well you can run{' '}
                <code>$ pnpm start:with-angular</code> instead of{' '}
                <code>$ pnpm start</code>.
              </p>

              <p>Happy coding!</p>
            </div>
          ) : (
            <Start />
          )
        }
      />

      <Route path="*" element={<ThrowNotFound />} />
    </RootRoutes>
  );
};

/**
 * Redirects every URL and replaces `from` with `to`, keeping
 * the rest of the path, the query string and the hash.
 */
const RedirectPrefix: FC<{ from: string; to: string }> = ({ from, to }) => {
  const { pathname, search, hash } = useLocation();
  return (
    <Navigate
      replace
      to={{ pathname: pathname.replace(from, to), search, hash }}
    />
  );
};

const RedirectOldNetworkPointPages: FC = () => {
  const { oldNetworkPointId } = useParams<{ oldNetworkPointId: string }>();
  const { pathname } = useLocation();
  const to = pathname.replace(
    `/network-points/${oldNetworkPointId}/`,
    `/transport/network-points/${oldNetworkPointId}/`
  );
  return <Navigate replace to={to} />;
};

const RedirectToContractDetailsForTso: FC = () => {
  const { shipperId, contractId, contractType } = useParams<{
    shipperId: string;
    contractId: string;
    contractType: string;
  }>();
  return (
    <Navigate
      replace
      to={`/dashboard/shippers/${shipperId}/contracts/${contractType}/${contractId}/details`}
    />
  );
};

const RedirectToContractDetailsForShipper: FC = () => {
  const { operatorUuid, contractId, contractType } = useParams<{
    operatorUuid: string;
    contractId: string;
    contractType: string;
  }>();
  return (
    <Navigate
      replace
      to={`/dashboard/operators/assignments/${operatorUuid}/contract/${contractType}/${contractId}/details`}
    />
  );
};

const RedirectTtpcModulationServiceAuction: FC = () => {
  const { auctionId } = useParams<{ auctionId: string }>();
  return <Navigate replace to={`/modulation-service-auctions/${auctionId}`} />;
};

const RedirectMailToSurrenderDetails: FC = () => {
  const { surrenderRequestId } = useParams<{ surrenderRequestId: string }>();
  const surrenderRoot = useSurrenderRoot();
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  if (!surrenderRoot.response) return <PageSpinner />;

  return (
    <Navigate
      replace
      to={
        !surrenderRoot.response.data._links.isSurrenderForAllEnabled
          ? `/reporting/surrenders/${surrenderRequestId}`
          : monolithUser?.isTso
            ? `/reporting/shipper-transactions/surrender/details/${surrenderRequestId}`
            : `/reporting/my-transactions/surrender/details/${surrenderRequestId}`
      }
    />
  );
};
const RedirectLegacyToRefactoredSurrenderCamaCreate = () => {
  const surrenderRoot = useSurrenderRoot();
  const { transactionId } = useParams<{ transactionId: string }>();

  if (!surrenderRoot.response) return <PageSpinner />;

  return !surrenderRoot.response.data._links.isSurrenderForAllEnabled ? (
    <CreateLegacySurrenderRequest />
  ) : (
    <Navigate
      replace
      to={`/reporting/my-transactions/surrender/create/cama/${transactionId}`}
    />
  );
};
const RedirectLegacyToRefactoredSurrenderDetails = () => {
  const { surrenderRequestId } = useParams<{ surrenderRequestId: string }>();
  const surrenderRoot = useSurrenderRoot();
  const monolithUser = useOptionalAuthenticatedMonolithUser();

  if (!surrenderRoot.response) return <PageSpinner />;

  return !surrenderRoot.response.data._links.isSurrenderForAllEnabled ? (
    <LegacySurrenderRequestDetail />
  ) : (
    <Navigate
      replace
      to={
        monolithUser?.isTso
          ? `/reporting/shipper-transactions/surrender/details/${surrenderRequestId}`
          : `/reporting/my-transactions/surrender/details/${surrenderRequestId}`
      }
    />
  );
};

const RedirectConversion: FC = () => {
  const { conversionId } = useParams<{ conversionId: string }>();
  return (
    <Navigate
      replace
      to={`/reporting/my-transactions/conversions/${conversionId}`}
    />
  );
};

const RedirectShippersInvoice: FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  return <Navigate replace to={`/shippers/invoices/${invoiceId}`} />;
};

const RedirectOperatorsInvoice: FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  return <Navigate replace to={`/operators/invoices/${invoiceId}`} />;
};

const RedirectShipperAdmins: FC = () => {
  const { shipperRefId } = useParams<{ shipperRefId: string }>();
  return <Navigate replace to={`/shippers/${shipperRefId}/admins`} />;
};

const RedirectShipper: FC = () => {
  const { shipperRefId } = useParams<{ shipperRefId: string }>();
  return <Navigate replace to={`/shippers/${shipperRefId}`} />;
};

const RedirectDealAllocationToBGA: FC = () => {
  const { dealId, direction } = useParams<{
    dealId: string;
    direction: string;
  }>();
  return (
    <Navigate
      replace
      to={`/reporting/my-transactions/deals/${dealId}/allocation-to-balancing-groups/${direction}`}
    />
  );
};

const RedirectTradeAllocationToBGA: FC = () => {
  const { tradeId, direction } = useParams<{
    tradeId: string;
    direction: string;
  }>();
  return (
    <Navigate
      replace
      to={`/reporting/my-transactions/trades/${tradeId}/allocation-to-balancing-groups/${direction}`}
    />
  );
};

const RedirectSecondaryBulletinBoard: FC = () => {
  const { bulletinBoardNoticeId } = useParams<{
    bulletinBoardNoticeId: string;
  }>();
  return (
    <Navigate
      replace
      to={`/transport/secondary/bulletin-board/${bulletinBoardNoticeId}`}
    />
  );
};
const RedirectSecondaryBulletinBoardConversation: FC = () => {
  const { bulletinBoardNoticeId, conversationId } = useParams<{
    bulletinBoardNoticeId: string;
    conversationId: string;
  }>();
  return (
    <Navigate
      replace
      to={`/transport/secondary/bulletin-board/${bulletinBoardNoticeId}?conversationId=${conversationId}`}
    />
  );
};
